import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import RoleSelector from "@/components/RoleSelector";
import RoleSelectionStep from "@/components/auth/RoleSelectionStep";
import LoginForm from "@/components/auth/LoginForm";
import AuthContainer from "@/components/auth/AuthContainer";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AuthProps {
  role?: "client" | "therapist";
}

const Auth = ({ role: routeRole }: AuthProps = {}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [roleSelectStep, setRoleSelectStep] = useState(false);
  const [selectedRole, setSelectedRole] = useState<"client" | "therapist" | null>(null);
  const [directLogin, setDirectLogin] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    isLoading,
    userId,
    showRoleSelector,
    errorMessage,
    handleAuthSubmit,
    setShowRoleSelector
  } = useAuth(selectedRole, isLogin);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Get user's role
          const { data: roleData, error: roleError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (roleError) {
            console.error("Error fetching user role:", roleError);
            return;
          }

          if (roleData) {
            // Always check for saved redirect path first
            const savedRedirectPath = localStorage.getItem('redirectAfterLogin');
            if (savedRedirectPath) {
              console.log(`Redirecting to saved path: ${savedRedirectPath}`);
              localStorage.removeItem('redirectAfterLogin');
              navigate(savedRedirectPath, { replace: true });
              return;
            }

            // Only redirect to /therapist or /journal if no redirect is present
            const redirectPath = roleData.role === 'client' ? '/journal' : '/therapist';
            console.log(`User already authenticated as ${roleData.role}, redirecting to ${redirectPath}`);
            navigate(redirectPath, { replace: true });
          } else {
            console.warn("User has no role assigned");
            await supabase.auth.signOut();
            toast.error("Your account needs a role assignment. Please contact support.");
          }
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    console.log("Auth page loaded, checking URL parameters and route props");
    const params = new URLSearchParams(location.search);
    const urlRole = params.get('role');
    const isDirect = params.get('directLogin') === 'true';
    const redirectTo = location.state?.redirectTo || params.get('redirect');

    // Store redirect path if present
    if (redirectTo) {
      localStorage.setItem('redirectAfterLogin', redirectTo);
    }

    // If a role is specified in the URL or props, skip role selection and go straight to login for that role
    if (urlRole === 'client' || urlRole === 'therapist' || routeRole) {
      setSelectedRole(urlRole === 'client' || urlRole === 'therapist' ? urlRole : routeRole || null);
      setRoleSelectStep(false);
      setDirectLogin(true);
    } else if (location.pathname === '/client-login') {
      setSelectedRole('client');
      setRoleSelectStep(false);
      setDirectLogin(true);
      localStorage.setItem('redirectAfterLogin', '/journal');
    } else {
      // No role specified, show role selection
      setSelectedRole(null);
      setRoleSelectStep(true);
      setDirectLogin(false);
    }
  }, [location.search, location.pathname, routeRole, location.state]);

  // Store redirect param in localStorage if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect) {
      localStorage.setItem('redirectAfterLogin', redirect);
    }
  }, [location.search]);

  const handleRoleSelect = (role: "client" | "therapist") => {
    console.log(`Selected role: ${role}`);
    setSelectedRole(role);
    setRoleSelectStep(false);
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
  };

  const getPageContent = () => {
    if (directLogin || routeRole || location.pathname === '/client-login') {
      const roleText = selectedRole === "therapist" ? "Therapist" : selectedRole === "client" ? "Journaler" : "Client";
      
      return {
        title: isLogin ? `${roleText} Login` : `Register as ${roleText}`,
        description: isLogin 
          ? (selectedRole === 'client' ? 'Continue your wellness journey' : `Continue your wellness journey as a ${roleText.toLowerCase()}`)
          : (selectedRole === 'client' ? 'Start your wellness journey' : `Start your wellness journey as a ${roleText.toLowerCase()}`),
        content: (
          <LoginForm 
            isLogin={isLogin}
            selectedRole={selectedRole}
            onSubmit={handleAuthSubmit}
            onToggleAuthMode={toggleAuthMode}
            loading={isLoading}
            errorMessage={errorMessage}
            directLogin={true}
          />
        )
      };
    } 
    else if (roleSelectStep) {
      return {
        title: "",
        description: "",
        content: <RoleSelectionStep onRoleSelect={handleRoleSelect} />
      };
    } else if (showRoleSelector && userId) {
      return {
        title: "Select Your Role",
        description: "Choose your role to continue",
        content: <RoleSelector userId={userId} onComplete={() => {}} />
      };
    } else {
      const roleText = selectedRole === "therapist" ? "Therapist" : selectedRole === "client" ? "Journaler" : "Client";
      const title = selectedRole 
        ? (isLogin ? `${roleText} Login` : `Register as ${roleText}`)
        : (isLogin ? "Welcome Back" : "Join Us");
      
      const description = selectedRole
        ? (isLogin ? (selectedRole === 'client' ? 'Continue your wellness journey' : `Continue your wellness journey as a ${roleText.toLowerCase()}`) : (selectedRole === 'client' ? 'Start your wellness journey' : `Start your wellness journey as a ${roleText.toLowerCase()}`))
        : (isLogin ? "Continue your wellness journey" : "Start your wellness journey");
      
      return {
        title: title,
        description: description,
        content: (
          <LoginForm 
            isLogin={isLogin}
            selectedRole={selectedRole}
            onSubmit={handleAuthSubmit}
            onToggleAuthMode={toggleAuthMode}
            loading={isLoading}
            errorMessage={errorMessage}
            directLogin={false}
          />
        )
      };
    }
  };

  const { title, description, content } = getPageContent();

  return (
    <>
      <AuthContainer title={title} description={description}>
        {content}
      </AuthContainer>
      <div style={{ position: 'fixed', bottom: 12, right: 16, zIndex: 50 }} className="text-xs text-gray-400 select-none pointer-events-none">
        V1.0.001
      </div>
    </>
  );
};

export default Auth;
